import { bus } from '../core/event-bus.js';
import { store } from '../core/state-store.js';

let _lastDisplayedXml = '';

export function initVastXmlInspector() {
  const treeContainer = document.getElementById('xml-tree');
  const fetchBtn = document.getElementById('xml-fetch-btn');
  const copyBtn = document.getElementById('xml-copy-btn');

  fetchBtn.addEventListener('click', () => {
    _displayCurrentXml(treeContainer);
  });

  copyBtn?.addEventListener('click', async () => {
    if (!_lastDisplayedXml) {
      const original = copyBtn.textContent;
      copyBtn.textContent = 'No XML to Copy';
      setTimeout(() => { copyBtn.textContent = original; }, 1500);
      return;
    }

    try {
      await navigator.clipboard.writeText(_lastDisplayedXml);
      const original = copyBtn.textContent;
      copyBtn.textContent = 'Copied!';
      setTimeout(() => { copyBtn.textContent = original; }, 1500);
    } catch {
      const original = copyBtn.textContent;
      copyBtn.textContent = 'Copy Failed';
      setTimeout(() => { copyBtn.textContent = original; }, 1500);
    }
  });

  bus.on('ad:event', ({ type }) => {
    if (type === 'adtagloaded') {
      _displayCurrentXml(treeContainer);
    }
  });

  bus.on('session:started', () => {
    _lastDisplayedXml = '';
    treeContainer.innerHTML = '<p class="placeholder-text">Load a VAST tag to inspect its XML structure.</p>';
  });
  bus.on('session:reset', () => {
    _lastDisplayedXml = '';
    treeContainer.innerHTML = '<p class="placeholder-text">Load a VAST tag to inspect its XML structure.</p>';
  });
}

function _displayCurrentXml(container) {
  const rawXml = store.state.rawXml;
  if (rawXml) {
    _parseAndDisplay(rawXml, container);
    return;
  }

  const tagUrl = store.state.adTagUrl || document.getElementById('tag-url-input')?.value;
  if (tagUrl) {
    _fetchAndDisplay(tagUrl, container);
  }
}

function _parseAndDisplay(xmlText, container) {
  _lastDisplayedXml = xmlText;
  container.innerHTML = '<p class="placeholder-text">Parsing VAST XML...</p>';

  try {
    const doc = new DOMParser().parseFromString(xmlText, 'text/xml');

    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      container.innerHTML = '';
      const pre = document.createElement('pre');
      pre.style.cssText = 'color:var(--red);padding:8px;';
      pre.textContent = 'XML Parse Error:\n' + parserError.textContent;
      container.appendChild(pre);

      const rawBlock = document.createElement('pre');
      rawBlock.style.cssText = 'color:var(--text-secondary);padding:8px;border-top:1px solid var(--border);margin-top:8px;';
      rawBlock.textContent = xmlText;
      container.appendChild(rawBlock);
      return;
    }

    container.innerHTML = '';
    const tree = _renderNode(doc.documentElement, 0);
    container.appendChild(tree);
  } catch (e) {
    container.innerHTML = `<p class="placeholder-text">Error parsing VAST XML: ${e.message}</p>`;
  }
}

async function _fetchAndDisplay(url, container) {
  container.innerHTML = '<p class="placeholder-text">Fetching VAST XML...</p>';

  try {
    const response = await fetch(url);
    if (!response.ok) {
      container.innerHTML = `<p class="placeholder-text">HTTP ${response.status}: ${response.statusText}</p>`;
      return;
    }

    const text = await response.text();
    _lastDisplayedXml = text;
    _parseAndDisplay(text, container);
  } catch (e) {
    container.innerHTML = `<p class="placeholder-text">Error fetching VAST: ${e.message}</p>`;
  }
}

function _renderNode(node, depth) {
  const wrapper = document.createElement('div');
  wrapper.className = 'xml-node';
  wrapper.style.marginLeft = `${depth * 16}px`;

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent.trim();
    if (text) {
      const span = document.createElement('span');
      span.className = 'xml-text';
      span.textContent = text;
      wrapper.appendChild(span);
    }
    return wrapper;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return wrapper;

  // Opening tag
  const tagLine = document.createElement('div');
  tagLine.className = 'xml-node-toggle';

  let tagHtml = `<span class="xml-tag">&lt;${node.tagName}</span>`;
  for (const attr of node.attributes) {
    tagHtml += ` <span class="xml-attr">${attr.name}</span>=<span class="xml-value">"${_escapeHtml(attr.value)}"</span>`;
  }

  const children = Array.from(node.childNodes).filter(c =>
    c.nodeType === Node.ELEMENT_NODE || (c.nodeType === Node.TEXT_NODE && c.textContent.trim())
  );

  if (children.length === 0) {
    tagHtml += `<span class="xml-tag"> /&gt;</span>`;
    tagLine.innerHTML = tagHtml;
    wrapper.appendChild(tagLine);
    return wrapper;
  }

  // Check if only text content
  if (children.length === 1 && children[0].nodeType === Node.TEXT_NODE) {
    const text = children[0].textContent.trim();
    tagHtml += `<span class="xml-tag">&gt;</span>`;
    tagHtml += `<span class="xml-text">${_escapeHtml(text)}</span>`;
    tagHtml += `<span class="xml-tag">&lt;/${node.tagName}&gt;</span>`;
    tagLine.innerHTML = tagHtml;
    wrapper.appendChild(tagLine);
    return wrapper;
  }

  tagHtml += `<span class="xml-tag">&gt;</span>`;
  tagLine.innerHTML = tagHtml;
  wrapper.appendChild(tagLine);

  const childContainer = document.createElement('div');
  childContainer.className = 'xml-children';

  for (const child of children) {
    childContainer.appendChild(_renderNode(child, depth + 1));
  }

  wrapper.appendChild(childContainer);

  // Toggle collapse
  tagLine.addEventListener('click', (e) => {
    const selectedText = window.getSelection?.()?.toString();
    if (selectedText && selectedText.trim()) {
      return;
    }
    e.stopPropagation();
    const isCollapsed = childContainer.style.display === 'none';
    childContainer.style.display = isCollapsed ? '' : 'none';
    tagLine.style.opacity = isCollapsed ? '1' : '0.6';
  });

  // Closing tag
  const closingTag = document.createElement('div');
  closingTag.style.marginLeft = `${depth * 16}px`;
  closingTag.innerHTML = `<span class="xml-tag">&lt;/${node.tagName}&gt;</span>`;
  wrapper.appendChild(closingTag);

  return wrapper;
}

function _escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
